import React, { useEffect, useState } from 'react';
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from 'react-redux';
import { MdOutlineClose, MdSaveAs, MdOutlineSave } from 'react-icons/md';
import { FaPlus } from 'react-icons/fa';
import AsyncSelect from 'react-select/async';
import { getItems } from 'redux/erp/itemSlice';
import ItemForm from './ItemForm';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { Drawer, Button, TwIconButton, FormRow } from 'components/common';

import { INPUT_CLS } from 'helpers/formStyles';
const ItemDimensionForm = ({ open, setOpen, record, title, onSave }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue } = useForm();
  const [showItemForm, setShowItemForm] = useState(false);

  const dispatch = useDispatch();

  const handleItemChange = (selectedOption) => {
    setValue("item_id", selectedOption ? selectedOption.value : null, { shouldValidate: true });
  };

  useEffect(() => {
    if (record) {
      Object.entries(record).forEach(([key, value]) => {
        setValue(key, value);
      })
    }
  }, [setValue, record, open]);

  const loadItemOptions = async (inputValue) => {
    try {
      const params = { startRow: 0, endRow: 50 };
      if (inputValue && inputValue.length > 2) params.name_cont = inputValue;

      const response = await dispatch(getItems(params)).unwrap();
      const data = response.rows;
      return Array.isArray(data)
        ? data.map(item => item.attributes).map(item => ({ value: item.id, label: item.code }))
        : [];
    } catch (error) {
      return [];
    }
  }

  // Returns a submit handler; `saveAndClose` decides whether to close or reset.
  const submit = (saveAndClose) => handleSubmit(async (data) => {
    try {
      await onSave(data);
      saveAndClose ? handleClose() : resetForm();
    } catch (error) {
      toast.error(error.message);
    }
  });

  const handleNewItem = () => setShowItemForm(true);

  const resetForm = () => {
    setValue("weight", "");
    setValue("building", "");
    setValue("remark", "");
  }

  const handleClose = () => setOpen(false);

  const defaultItemOption = record && record.item_id && record.item?.code
    ? { value: record.item_id, label: record.item.code }
    : null;

  return (
    <>
      <Drawer
        show={open}
        onHide={handleClose}
        placement="right"
        backdropClick={false}
        width="50%"
        title={title}
        footer={
          <div className="tw-flex tw-justify-end tw-gap-2">
            <Button variant="danger" size="sm" leftIcon={MdOutlineClose} onClick={handleClose}>Close</Button>
            {!record && (
              <Button variant="secondary" size="sm" leftIcon={MdSaveAs} onClick={submit(false)}>Save and continue</Button>
            )}
            <Button variant="primary" size="sm" leftIcon={MdOutlineSave} onClick={submit(true)}>
              {record ? "Update" : "Save and Close"}
            </Button>
          </div>
        }
      >
        <form onSubmit={submit(true)}>
          <FormRow label="Name" required error={errors.name?.message}>
            <input placeholder="Name" className={INPUT_CLS} {...register("name", { required: "Name is required." })} />
          </FormRow>

          <FormRow label="ItemId" required error={errors.item_id?.message}>
            <div className="tw-flex tw-items-center tw-gap-1.5">
              <div className="tw-flex-1 tw-min-w-0">
                <Controller
                  control={control}
                  name="item_id"
                  rules={{ required: "ItemId is required" }}
                  render={() => (
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      loadOptions={loadItemOptions}
                      defaultValue={defaultItemOption}
                      closeMenuOnSelect={true}
                      placeholder='Select...'
                      classNamePrefix="react-select"
                      onChange={handleItemChange}
                    />
                  )}
                />
              </div>
              <TwIconButton icon={FaPlus} title="New item" variant="secondary" onClick={handleNewItem} />
            </div>
          </FormRow>

          <FormRow label="Thick/Job" error={errors.thick_job?.message}>
            <input placeholder="Thick/Job" className={INPUT_CLS} {...register("thick_job")} />
          </FormRow>

          <FormRow label="Width" error={errors.width?.message}>
            <input placeholder="Width" className={INPUT_CLS} {...register("width")} />
          </FormRow>

          <FormRow label="Length" error={errors.length?.message}>
            <input placeholder="Length" className={INPUT_CLS} {...register("length")} />
          </FormRow>

          <FormRow label="Length partmark" error={errors.length_partmark?.message}>
            <input placeholder="Length partmark" className={INPUT_CLS} {...register("length_partmark")} />
          </FormRow>
        </form>
      </Drawer>

      {showItemForm && (
        <ItemForm
          open={showItemForm}
          setOpen={setShowItemForm}
          title="New Item"
        />
      )}
    </>
  )
}

ItemDimensionForm.propTypes = {
  open: PropTypes.bool.isRequired,
  record: PropTypes.object,
  title: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  setOpen: PropTypes.func.isRequired
}

export default ItemDimensionForm;
